/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * PhoneVerificationSheet — the PRIMARY sign-in surface (full-screen).
 *
 * MyAmbits is phone-only: a verified phone number IS the identity. This is both
 * the logged-out sign-in entry (App.tsx renders it for the `!user` route, after
 * Welcome) and the AppPhoneGate verification surface for any session still
 * missing /users/{uid}/phoneNumber.
 *
 * Full-screen steps (OnboardingShell layout):
 *   1. enter-phone — country-code selector (default US +1), national number
 *      input, libphonenumber-js validation, "Send code" CTA.
 *   2. verifying  — a calm "Verifying your number…" state that holds steady
 *      through the brief Firebase reCAPTCHA Safari round-trip on native, so the
 *      UI never flashes back to the raw phone form.
 *   3. enter-code  — 6-digit OTP (input-otp), resend, "Verify" CTA.
 *
 * OTP delivery:
 *   - iOS (native) → @capacitor-firebase/authentication signInWithPhoneNumber:
 *     APNs-receipt verification. Firebase may briefly present its own reCAPTCHA
 *     "verify app" Safari sheet (its standard fallback); the callback returns to
 *     AppDelegate (Auth.canHandle), then `phoneCodeSent` emits the
 *     verificationId and we advance to the code step.
 *   - non-native (web/preview) → JS SDK PhoneAuthProvider.verifyPhoneNumber +
 *     a visible RecaptchaVerifier.
 *
 * The screen performs NO auth mutations itself. It builds a phone
 * AuthCredential and hands it to AuthContext.verifyPhone, which owns the
 * signInWithCredential call (phone is the PRIMARY sign-in — it creates the
 * account on first verification, or returns the existing one). Firebase Auth
 * enforces one account per phone number, so sign-in lands deterministically on
 * the right account — there is NO link / canonical-swap / collision machinery
 * (removed with OAuth in the phone-only migration).
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  type AuthCredential,
} from "firebase/auth";
import {
  Loader2,
  MessageSquareText,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import * as Sentry from "@sentry/react";
import { auth } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import {
  useApnsRegistration,
  type ApnsStatus,
} from "@/hooks/useApnsRegistration";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import OnboardingShell from "@/components/onboarding/OnboardingShell";
import {
  COUNTRY_OPTIONS,
  DEFAULT_COUNTRY,
  normalizeToE164,
  type CountryOption,
} from "@/utils/phone";
import { phoneVerifyErrorMessage } from "@/utils/phoneVerifyError";
import { isTestPhoneNumber } from "@/utils/testPhoneNumbers";
import {
  recordSendFailed,
  recordApnsFallbackToWeb,
  recordMissingVerificationId,
  type OtpInstrumentationSink,
} from "@/utils/safecircle/otpInstrumentation";

type Step = "enter-phone" | "verifying" | "enter-code";

const isNative = Capacitor.isNativePlatform();

// Sentry adapter for the pure OTP instrumentation helpers. Module-level:
// stable identity, matches the sink pattern in useSOSEvent / WalkMeHomeContext.
const otpSink: OtpInstrumentationSink = {
  addBreadcrumb: (b) => Sentry.addBreadcrumb(b),
  captureMessage: (message, ctx) => Sentry.captureMessage(message, ctx),
  captureException: (err, ctx) => Sentry.captureException(err, ctx),
};

// Seconds to disable "Resend code" after a successful send. Slows how fast a
// user can hammer Firebase's per-number SMS quota into `auth/too-many-requests`.
const RESEND_COOLDOWN_SECONDS = 45;

interface Props {
  /**
   * Non-destructive exit. When provided, surfaces an "I'll do this later"
   * affordance so a user who reached the gate by accident can return to the
   * rest of the app (typically the Map / safety core) WITHOUT verifying and
   * WITHOUT logging out. The gate parent decides what "back" means.
   */
  onExit?: () => void;
}

export default function PhoneVerificationSheet({ onExit }: Props = {}) {
  const { verifyPhone, resumeSession, logout } = useAuth();

  // Soft-logout re-entry: if a Firebase session is still live (soft logout kept
  // it), offer one-tap "Continue as +1 …" so a returning user skips a fresh SMS
  // (and can't trip Firebase's anti-abuse limit on logout/login cycles). Read
  // once at mount — the session doesn't change while this screen is up.
  const livePhone = auth.currentUser?.phoneNumber ?? null;
  const [resuming, setResuming] = useState(false);

  const handleContinueAs = useCallback(async () => {
    setResuming(true);
    try {
      const resumed = await resumeSession();
      if (!resumed) setResuming(false); // no session — fall through to phone form
    } catch {
      setResuming(false);
    }
  }, [resumeSession]);

  const handleUseDifferentNumber = useCallback(async () => {
    // Hard logout fully revokes the kept session so a different number can sign
    // in cleanly; the screen stays mounted on the phone form.
    await logout({ hard: true });
  }, [logout]);
  // APNs readiness gate. On native, "Send code" waits for FIRAuth to hold the
  // APNs token so verifyPhoneNumber uses the silent-push path and the flaky web
  // reCAPTCHA never appears. Off iOS, waitForApns resolves "ready" instantly.
  const { waitForApns } = useApnsRegistration();
  const [step, setStep] = useState<Step>("enter-phone");
  const [country, setCountry] = useState<CountryOption>(DEFAULT_COUNTRY);
  const [nationalNumber, setNationalNumber] = useState("");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Resend cooldown — seconds remaining before "Resend code" re-enables. Set on
  // every successful send; ticks down to 0. Guards both the resend affordance
  // and a duplicate "Send code" while a code is already in flight/recent.
  const [cooldown, setCooldown] = useState(0);


  // Native APNs readiness for the "Send code" gate. We track the RESOLVED
  // status (not just "settled") so the button can hold the user until the
  // native silent-push path is confirmed usable — that's what stops the
  // cold-launch race from dropping a fast tapper onto the flaky web reCAPTCHA:
  //   - "pending"     → token not confirmed yet → button disabled ("Preparing…")
  //   - "ready"       → FIRAuth holds the token → native silent-push path
  //   - "unavailable" → token will never arrive (denied / genuine timeout) →
  //                     enable anyway so the user isn't stranded; sendCode's
  //                     live re-check then falls to the web reCAPTCHA last-resort
  //                     net. This is reached only when there's truly no token —
  //                     NEVER from the cold-launch race (option 1b).
  // Off iOS there is no APNs concept, so we start "ready".
  const [apnsStatus, setApnsStatus] = useState<ApnsStatus>(
    isNative ? "pending" : "ready",
  );
  // Button is usable once readiness has SETTLED either way — only "pending"
  // (still confirming) holds it. Gating on "ready" alone would trap a
  // denied-notifications user; "unavailable" intentionally falls through to the
  // web net per the design decision.
  const apnsSettled = apnsStatus !== "pending";

  // Verification id from PhoneAuthProvider.verifyPhoneNumber; handleConfirmCode
  // builds the credential from it.
  const verificationIdRef = useRef<string | null>(null);
  const e164Ref = useRef<string | null>(null);

  // reCAPTCHA (JS SDK) state — only used on the non-native web/preview path.
  const verifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaElRef = useRef<HTMLDivElement | null>(null);

  // Native phone-auth event listeners (phoneCodeSent / phoneVerificationFailed).
  const codeSentSubRef = useRef<PluginListenerHandle | null>(null);
  const failedSubRef = useRef<PluginListenerHandle | null>(null);

  const phoneInputRef = useRef<HTMLInputElement | null>(null);

  // The full E.164 candidate — country dial code is conveyed via the selected
  // region, so we normalize the national digits against that region. A user
  // who types a leading "+" still parses correctly regardless of selection.
  const e164Candidate = normalizeToE164(nationalNumber, country.region);
  const phoneValid = e164Candidate !== null;

  useEffect(() => {
    if (step === "enter-phone") {
      // Fire focus inside a rAF so it lands after React commits the step.
      requestAnimationFrame(() => phoneInputRef.current?.focus());
    }
  }, [step]);

  // Resend cooldown countdown — one interval while seconds remain.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => {
      setCooldown((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve native APNs readiness in the background as soon as the screen
  // mounts. Priming already ran at app-mount, so the token is usually in flight;
  // this awaits the authoritative confirmation and records the RESOLVED status.
  // While it's still "pending" the button stays disabled ("Preparing…"), which
  // holds a fast cold-launch tapper until the native silent-push path is ready —
  // the fix for the intermittent white reCAPTCHA. "ready" → native path;
  // "unavailable" (denied / genuine timeout) → enable + web last-resort net.
  useEffect(() => {
    if (!isNative) return;
    let cancelled = false;
    void waitForApns().then((status) => {
      if (!cancelled) setApnsStatus(status);
    });
    return () => {
      cancelled = true;
    };
  }, [waitForApns]);

  const teardownNativeListeners = useCallback(async () => {
    await codeSentSubRef.current?.remove().catch(() => undefined);
    await failedSubRef.current?.remove().catch(() => undefined);
    codeSentSubRef.current = null;
    failedSubRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      verifierRef.current?.clear();
      verifierRef.current = null;
      void teardownNativeListeners();
    };
  }, [teardownNativeListeners]);

  // Native OTP send. The `phoneCodeSent` listener advances to the code step
  // (and clears the "verifying" hold); `phoneVerificationFailed` returns to the
  // phone step with a friendly error. Firebase may briefly show its own
  // reCAPTCHA Safari sheet between the call and the event — the "verifying"
  // step keeps our UI steady across that round-trip.
  const sendCodeNative = useCallback(
    async (e164: string) => {
      await teardownNativeListeners();
      const sentSub = await FirebaseAuthentication.addListener(
        "phoneCodeSent",
        (event) => {
          verificationIdRef.current = event.verificationId;
          setSubmitting(false);
          setStep("enter-code");
        },
      );
      codeSentSubRef.current = sentSub;
      const failedSub = await FirebaseAuthentication.addListener(
        "phoneVerificationFailed",
        (event) => {
          verificationIdRef.current = null;
          setSubmitting(false);
          // Return to the phone form FIRST so the user never sits on a blank
          // "verifying" screen, THEN surface the message.
          setStep("enter-phone");
          // The native event carries only a raw `message` (no code). This is
          // where iOS `auth/too-many-requests` — the "white page" — arrives
          // with NO console and NO Sentry today. Capture it (rate-limit vs
          // other classified inside). UI copy unchanged. No phone number is
          // passed — only the server message + path.
          recordSendFailed(otpSink, { message: event.message, path: "native" });
          setError(phoneVerifyErrorMessage({ message: event.message }));
        },
      );
      failedSubRef.current = failedSub;
      await FirebaseAuthentication.signInWithPhoneNumber({
        phoneNumber: e164,
        skipNativeAuth: true,
      });
    },
    [teardownNativeListeners],
  );

  // Web/preview only. A VISIBLE ("normal") reCAPTCHA — the invisible variant
  // can't silently pass the challenge in a webview and errors with
  // auth/internal-error. Native uses the plugin instead and never calls this.
  const ensureVerifier = useCallback((): RecaptchaVerifier => {
    if (verifierRef.current) return verifierRef.current;
    if (!recaptchaElRef.current) {
      throw new Error("recaptcha-container-missing");
    }
    const verifier = new RecaptchaVerifier(auth, recaptchaElRef.current, {
      size: "normal",
    });
    verifierRef.current = verifier;
    return verifier;
  }, []);

  const sendCodeWeb = useCallback(
    async (e164: string) => {
      const verifier = ensureVerifier();
      // verifyPhoneNumber sends the OTP and returns a verificationId WITHOUT
      // signing anyone in — AuthContext.verifyPhone does the
      // signInWithCredential later (phone is the PRIMARY sign-in).
      const provider = new PhoneAuthProvider(auth);
      const verificationId = await provider.verifyPhoneNumber(e164, verifier);
      verificationIdRef.current = verificationId;
      setStep("enter-code");
    },
    [ensureVerifier],
  );

  const sendCode = useCallback(async () => {
    const e164 = e164Candidate;
    if (!e164) {
      setError("Enter a valid phone number.");
      return;
    }
    // Cooldown / in-flight guard: a code was just sent. Block the duplicate so
    // rapid taps and quick re-sends don't burn Firebase's per-number SMS quota.
    if (cooldown > 0 || submitting) return;
    setError(null);
    setCode("");
    setSubmitting(true);
    e164Ref.current = e164;
    verificationIdRef.current = null;

    // Firebase Console "test" numbers send no real SMS and don't count against
    // the anti-abuse limit. The native plugin honors them directly — no APNs
    // silent-push and no reCAPTCHA — so we route them through the native path
    // but SKIP the APNs gate (a test number needs no token).
    const isTest = isTestPhoneNumber(e164);

    try {
      if (isNative && isTest) {
        // Test number on device: native plugin returns phoneCodeSent instantly,
        // no SMS, no reCAPTCHA, no APNs needed.
        setStep("verifying");
        await sendCodeNative(e164);
      } else if (isNative) {
        // Authoritative LIVE gate, decided BEFORE we change steps: does FIRAuth
        // hold the APNs token right now? "ready" → native silent-push path (no
        // reCAPTCHA). "unavailable" (denied/timeout) → web verifier fallback so
        // we never strand the user. Native verifyPhoneNumber is only called when
        // the token is confirmed present, so it can't nil-crash.
        const liveStatus = await waitForApns();
        if (liveStatus === "ready") {
          // Silent-push path: hold a calm "Verifying…" screen while Firebase
          // does its round-trip — `phoneCodeSent` advances to the code step.
          // No reCAPTCHA appears. submitting stays true (native resolves as
          // soon as the request is queued).
          setStep("verifying");
          await sendCodeNative(e164);
        } else {
          // Fallback: stay on the phone form so the reCAPTCHA widget (rendered
          // there) is visible for the web verifier. Breadcrumb the silent
          // degrade (APNs unavailable → web reCAPTCHA) so the trail explains
          // why a reCAPTCHA appeared on iOS.
          recordApnsFallbackToWeb(otpSink, liveStatus);
          await sendCodeWeb(e164);
          setSubmitting(false);
        }
      } else {
        await sendCodeWeb(e164);
        setSubmitting(false);
      }
      // Successful send (native queued or web returned a verificationId) —
      // start the resend cooldown.
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setSubmitting(false);
      // Any synchronous send failure returns to the phone form.
      if (isNative) setStep("enter-phone");
      const code = (err as { code?: string })?.code ?? "";
      const message = (err as { message?: string })?.message;
      if (
        code !== "auth/invalid-phone-number" &&
        code !== "auth/too-many-requests"
      ) {
        console.error("[PhoneVerificationSheet] send code failed:", err);
      }
      // The send path had NO Sentry at all — too-many-requests (the "white
      // page") logged nothing. Capture every send failure (rate-limit vs other
      // classified inside). UI copy unchanged; no phone number passed.
      recordSendFailed(otpSink, {
        code,
        message,
        path: isNative ? "native" : "web",
      });
      setError(phoneVerifyErrorMessage({ code, message }));
      verifierRef.current?.clear();
      verifierRef.current = null;
    }
  }, [e164Candidate, sendCodeWeb, sendCodeNative, waitForApns, cooldown, submitting]);

  const handleConfirmCode = useCallback(async () => {
    const trimmed = code.trim();
    if (trimmed.length < 6) {
      setError("Enter the 6-digit code.");
      return;
    }
    const e164 = e164Ref.current;
    if (!e164) {
      setError("Session lost. Please retry.");
      setStep("enter-phone");
      return;
    }
    setError(null);
    setSubmitting(true);

    // Build the phone credential WITHOUT signing in here — AuthContext.
    // verifyPhone owns the signInWithCredential call (phone is the PRIMARY
    // sign-in: it creates the account on first verification). No
    // auth.currentUser precondition: the user is not signed in until
    // verifyPhone runs.
    let phoneCred: AuthCredential;
    try {
      const verificationId = verificationIdRef.current;
      if (!verificationId) throw new Error("missing-verification-id");
      phoneCred = PhoneAuthProvider.credential(verificationId, trimmed);
    } catch (err) {
      setSubmitting(false);
      console.error(
        "[PhoneVerificationSheet] missing verification id/confirmation:",
        err,
      );
      // Console-only today: the native phoneCodeSent never fired (or the ref
      // was cleared), so verify dead-ends. Capture it.
      recordMissingVerificationId(otpSink);
      setError("Couldn't verify. Request a new code.");
      setStep("enter-phone");
      return;
    }

    try {
      // verifyPhone is the single entry point: signInWithCredential lands on
      // the phone's account (created on first verify). AuthContext's pipeline
      // then runs profile + EULA gating and the gate unmounts us.
      await verifyPhone(phoneCred, e164);
      setSubmitting(false);
    } catch (err) {
      setSubmitting(false);
      const errCode = (err as { code?: string })?.code ?? "";
      const errMsg = String((err as { message?: string })?.message ?? "").toLowerCase();
      Sentry.addBreadcrumb({
        category: "auth.phone",
        message: "confirmCode.error",
        level: "warning",
        data: { errCode, errMsgPreview: errMsg.slice(0, 120) },
      });
      if (errCode === "auth/invalid-verification-code") {
        setError("That code didn't match. Double-check and retry.");
      } else if (errCode === "auth/code-expired") {
        setError("Code expired. Request a new one.");
        setStep("enter-phone");
      } else {
        // The OTP was fine but sign-in failed for another reason (network,
        // etc.). Surface a friendly retry rather than stranding the user.
        console.error("[PhoneVerificationSheet] confirm failed:", err);
        setError("Couldn't sign you in. Check your connection and retry.");
      }
    }
  }, [code, verifyPhone]);

  const displayPhone = e164Candidate ?? `${country.dialCode} ${nationalNumber}`;

  // ── enter-phone ─────────────────────────────────────────────────────────
  if (step === "enter-phone") {
    return (
      <OnboardingFrame
        step={1}
        onBack={undefined}
        Icon={MessageSquareText}
        title="What's your number?"
        subtitle="We'll text you a 6-digit code so your friends know it's really you. Your number stays private."
        ctaLabel={apnsSettled ? "Send code" : "Preparing secure verification…"}
        ctaDisabled={!phoneValid || !apnsSettled}
        ctaBusy={submitting || (!apnsSettled && phoneValid)}
        onCta={sendCode}
        secondaryLabel={onExit ? "I'll do this later" : undefined}
        onSecondary={onExit}
        recaptchaRef={recaptchaElRef}
        error={error}
      >
        {/* Soft-logout re-entry — one tap back in, no SMS. */}
        {livePhone && (
          <div className="mb-5 rounded-2xl border border-[#E0D8F3] bg-white px-4 py-4 shadow-[0_8px_24px_-16px_rgba(124,82,245,0.5)]">
            <p className="text-sm text-[#6B5FA0]">Welcome back</p>
            <p className="mt-0.5 font-semibold text-[#2A2342]">{livePhone}</p>
            <button
              type="button"
              onClick={handleContinueAs}
              disabled={resuming}
              className="mt-3 flex h-[48px] w-full items-center justify-center rounded-2xl bg-[#7C52F5] text-base font-semibold text-white active:scale-[0.98] transition-transform disabled:opacity-60"
            >
              {resuming ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              ) : (
                "Continue"
              )}
            </button>
            <button
              type="button"
              onClick={handleUseDifferentNumber}
              disabled={resuming}
              className="mt-2 min-h-[44px] w-full py-1.5 text-sm font-medium text-[#6B5FA0] active:opacity-70 disabled:opacity-60"
            >
              Use a different number
            </button>
          </div>
        )}
        <div className="flex items-stretch gap-2.5">
          <label className="sr-only" htmlFor="country-select">
            Country code
          </label>
          <div className="relative flex items-center rounded-2xl border border-[#E0D8F3] bg-white px-3 shadow-[0_8px_24px_-16px_rgba(124,82,245,0.5)]">
            <span className="mr-1.5 text-lg" aria-hidden="true">
              {country.flag}
            </span>
            <span className="text-base font-semibold text-[#2A2342]">
              {country.dialCode}
            </span>
            <select
              id="country-select"
              value={country.region}
              onChange={(e) => {
                const next = COUNTRY_OPTIONS.find(
                  (c) => c.region === e.target.value,
                );
                if (next) {
                  setCountry(next);
                  setError(null);
                }
              }}
              className="absolute inset-0 cursor-pointer opacity-0"
              aria-label="Country code"
            >
              {COUNTRY_OPTIONS.map((c) => (
                <option key={c.region} value={c.region}>
                  {c.flag} {c.name} ({c.dialCode})
                </option>
              ))}
            </select>
          </div>
          <input
            ref={phoneInputRef}
            id="phone-input"
            type="tel"
            autoComplete="tel-national"
            inputMode="tel"
            value={nationalNumber}
            onChange={(e) => {
              setError(null);
              setNationalNumber(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && phoneValid && !submitting) sendCode();
            }}
            placeholder="(555) 123-4567"
            className="min-w-0 flex-1 rounded-2xl border border-[#E0D8F3] bg-white px-4 py-3.5 text-base text-[#2A2342] placeholder:text-[#A89FC4] shadow-[0_8px_24px_-16px_rgba(124,82,245,0.5)] focus:border-[#7C52F5] focus:outline-none focus:ring-2 focus:ring-[#7C52F5]/20"
          />
        </div>
      </OnboardingFrame>
    );
  }

  // ── verifying (native round-trip hold) ───────────────────────────────────
  if (step === "verifying") {
    return (
      <OnboardingFrame
        step={1}
        Icon={ShieldCheck}
        title="Verifying your number…"
        subtitle="Hang tight — confirming this is really your phone. This only takes a moment."
        error={error}
      >
        <div className="flex justify-center py-6">
          <Loader2
            className="h-8 w-8 animate-spin text-[#7C52F5]"
            aria-hidden="true"
          />
        </div>
      </OnboardingFrame>
    );
  }

  // ── enter-code ──────────────────────────────────────────────────────────
  return (
    <OnboardingFrame
      step={1}
      onBack={() => {
        setStep("enter-phone");
        setError(null);
      }}
      Icon={ShieldCheck}
      title="Almost there"
      subtitle={
        <>
          Pop in the 6-digit code we just sent to{" "}
          <span className="font-semibold text-[#2A2342]">{displayPhone}</span>.
        </>
      }
      ctaLabel="Verify"
      ctaDisabled={code.length < 6}
      ctaBusy={submitting}
      onCta={handleConfirmCode}
      secondaryLabel={
        submitting
          ? undefined
          : cooldown > 0
            ? `Resend code in ${cooldown}s`
            : "Resend code"
      }
      onSecondary={
        cooldown > 0
          ? undefined
          : () => {
              setStep("enter-phone");
              setError(null);
            }
      }
      recaptchaRef={recaptchaElRef}
      error={error}
    >
      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={code}
          onChange={(v) => {
            setError(null);
            setCode(v);
          }}
          containerClassName="gap-2"
          aria-label="Verification code"
          autoFocus
        >
          <InputOTPGroup className="gap-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot
                key={i}
                index={i}
                className="h-14 w-12 rounded-xl border-[#E0D8F3] bg-white text-xl font-semibold text-[#2A2342] shadow-[0_8px_24px_-16px_rgba(124,82,245,0.5)] first:rounded-xl last:rounded-xl"
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>
    </OnboardingFrame>
  );
}

/* ------------------------------------------------------------------------- */
/* OnboardingFrame — local wrapper that pairs OnboardingShell with the       */
/* shared icon-hero + title + subtitle + inline error block these two auth   */
/* steps use. Kept here (not exported) since only the phone steps need this  */
/* exact hero treatment; later 2B–2D screens compose OnboardingShell direct. */
/* ------------------------------------------------------------------------- */

function OnboardingFrame({
  step,
  onBack,
  Icon,
  title,
  subtitle,
  children,
  error,
  ctaLabel,
  ctaDisabled,
  ctaBusy,
  onCta,
  secondaryLabel,
  onSecondary,
  recaptchaRef,
}: {
  step: number;
  onBack?: () => void;
  Icon: LucideIcon;
  title: string;
  subtitle: ReactNode;
  children: ReactNode;
  error: string | null;
  ctaLabel: ReactNode;
  ctaDisabled?: boolean;
  ctaBusy?: boolean;
  onCta: () => void;
  secondaryLabel?: ReactNode;
  onSecondary?: () => void;
  recaptchaRef?: RefObject<HTMLDivElement>;
}) {
  // The onboarding flow is phone → OTP → EULA → profile → emergency →
  // discovery. The phone+OTP steps both report as step 1 of the user-facing
  // progress; the later screens carry their own step numbers.
  return (
    <OnboardingShell
      step={step}
      totalSteps={5}
      onBack={onBack}
      ariaLabel={title}
      ctaLabel={ctaLabel}
      ctaDisabled={ctaDisabled}
      ctaBusy={ctaBusy}
      onCta={onCta}
      secondaryLabel={secondaryLabel}
      onSecondary={onSecondary}
    >
      <div className="mx-auto flex max-w-md flex-col items-center text-center">
        <div className="mt-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#7C52F5]/12 text-[#7C52F5] shadow-[0_12px_30px_-16px_rgba(124,82,245,0.7)]">
          <Icon size={30} aria-hidden="true" />
        </div>
        <h1 className="mt-5 text-2xl font-bold tracking-tight text-[#2A2342]">
          {title}
        </h1>
        <p className="mt-2.5 text-[15px] leading-relaxed text-[#6B5FA0]">
          {subtitle}
        </p>
        <div className="mt-7 w-full text-left">{children}</div>
        {error && (
          <p className="mt-3 w-full text-left text-sm text-[#DC2626]" role="alert">
            {error}
          </p>
        )}
        {/* reCAPTCHA mount point. Web/preview always uses it. On native it
            stays empty (the silent-push APNs path needs no widget) UNLESS the
            APNs token never arrives — then sendCodeWeb is the safety-net
            fallback and needs this container to exist. Empty until a verifier
            renders into it, so it's inert when unused. */}
        {recaptchaRef && (
          <div ref={recaptchaRef} className="mt-5 flex w-full justify-center" />
        )}
      </div>
    </OnboardingShell>
  );
}
